import * as React from "react"

import { cn } from "@/lib/utils"

/* Cartão — arquivo do shadcn, reestilizado para a Colmeia.
 *
 * Vira uma CÉLULA: raio de 32px, campo de nata, sem borda e sem sombra. O que
 * o separa do chão é a diferença de campo, não um contorno.
 *
 * `Celula` (em componentes/celula.tsx) é o primitivo real do sistema, com
 * campo semântico e raio concêntrico. `Card` continua existindo porque o
 * shadcn o exporta em pedaços (header, title, description, action) que já
 * resolvem um layout comum, e porque telas já escritas o importam. */

function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "flex flex-col gap-5 rounded-2xl bg-card py-6 text-card-foreground",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      /* Título de CARTÃO, o segundo degrau da hierarquia (ver .titulo-cartao
         em globals.css). Estava em --t-sub, que é o degrau do bloco DENTRO do
         cartão: com o caixa alta fora do sistema, tamanho e peso passaram a
         ser os únicos separadores de nível e os dois não podiam mais empatar. */
      className={cn("titulo-cartao", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      /* Frase, não etiqueta: quem quer o título miúdo usa a classe `rotulo`.
         `apoio` traz junto o limite de 52ch, que é o que impede a descrição
         de virar uma linha de 180 caracteres num cartão largo. */
      className={cn("apoio", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
